import type { ReactNode } from 'react';
import { Check } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type FeatureBlockProps = {
  eyebrow?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  /** At most three, each a concrete behaviour ("Emails you the morning a match appears"). */
  checks?: ReactNode[];
  /** The real component or a screenshot of it, never an illustration of it. */
  media: ReactNode;
  /** Media on the left from md (alternate down a page). */
  reverse?: boolean;
  headingLevel?: 2 | 3;
  className?: string;
};

/** A marketing section that explains one feature next to the real component it describes. */
export function FeatureBlock({
  eyebrow,
  title,
  body,
  checks = [],
  media,
  reverse = false,
  headingLevel = 2,
  className,
}: FeatureBlockProps) {
  const Heading = `h${headingLevel}` as 'h2';
  return (
    <section className={cx('grid items-center gap-8 md:grid-cols-2 md:gap-12', className)}>
      <div className={cx('flex flex-col gap-4', reverse && 'md:order-2')}>
        {eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{eyebrow}</span> : null}
        <Heading className="type-display text-jc-ink">{title}</Heading>
        {body ? <p className="text-base text-jc-ink-secondary">{body}</p> : null}
        {checks.length ? (
          <ul className="flex flex-col gap-2">
            {checks.slice(0, 3).map((check, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-jc-ink">
                <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-jc-forest-soft text-jc-accent">
                  <Icon icon={Check} size="sm" />
                </span>
                {check}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className={cx('min-w-0', reverse && 'md:order-1')}>{media}</div>
    </section>
  );
}

export type CtaBandProps = {
  title: ReactNode;
  body?: ReactNode;
  /** The page's one primary action (a primary Button). */
  action: ReactNode;
  /** dark (default): the readout band, dark in every theme. page: a white card on the page colours (no dark band). */
  tone?: 'dark' | 'page';
  className?: string;
};

/** The dark band that ends a marketing page, holding its one primary action. Dark in every theme. */
export function CtaBand({ title, body, action, tone = 'dark', className }: CtaBandProps) {
  const page = tone === 'page';
  return (
    <section
      className={cx(
        'flex flex-col items-start gap-5 rounded-jc-container p-8 md:flex-row md:items-center md:p-12',
        page
          ? 'border border-jc-card-border bg-jc-surface text-jc-ink'
          : 'bg-jc-readout-bg text-jc-readout-ink',
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-2">
        <h2 className="type-title">{title}</h2>
        {body ? <p className={cx('text-base', page ? 'text-jc-ink-secondary' : 'text-jc-readout-muted')}>{body}</p> : null}
      </div>
      <div className="shrink-0">{action}</div>
    </section>
  );
}
